import { ENDPOINTS } from "utils/endpoints";
import { GET_API_CALL, PATCH_API_CALL, POST_API_CALL } from "utils/services";
import { APIResponseModal } from "utils/modals";
import { UPDATE_SERVICE_TYPE_REDUCER } from "store/actionTypes";
import SHOW_TOAST from "utils/showToast";
import {
  mapCategoryTypeItem,
  mapPaginatedResult,
  mapServiceTypeItem,
} from "utils/apiMappers";
import {
  SERVICE_CATEGORY,
  hasCategoryTypeFilter,
  toServiceCategory,
} from "utils/serviceCategory";
import {
  getAllCarTypesList,
  getCarpetType,
  getSofaType,
  getTankType,
} from "modules/utilityManagement/action";

/**
 * GET /api/v1/shinology/services/service-types?category&page&limit
 * `category` is mandatory: car=0, bike=1, sofa=2, tank=3, carpet=4
 */
export const getServiceTypesListing = () => {
  return (dispatch: Function, getState: Function) => {
    const { page, limit, category } = getState().ServiceTypeReducer;
    const resolvedCategory = toServiceCategory(category);

    const queryParams: string[] = [`category=${resolvedCategory}`];
    if (page) queryParams.push(`page=${page}`);
    if (limit) queryParams.push(`limit=${limit}`);

    dispatch({
      type: UPDATE_SERVICE_TYPE_REDUCER,
      payload: { loader: true },
    });

    GET_API_CALL(
      ENDPOINTS.SERVICE.TYPE_LISTING,
      `?${queryParams.join("&")}`,
      (response: APIResponseModal) => {
        if (response.isSuccess) {
          const dataToSave = mapPaginatedResult(
            response.data,
            mapServiceTypeItem
          );
          dispatch({
            type: UPDATE_SERVICE_TYPE_REDUCER,
            payload: { ...dataToSave, loader: false },
          });
        } else {
          dispatch({
            type: UPDATE_SERVICE_TYPE_REDUCER,
            payload: { loader: false, result: [] },
          });
        }
      }
    );
  };
};

/**
 * Loads the type dropdown for a category from its own listing API.
 * car -> carType, sofa -> sofa type, tank -> tank type, carpet -> carpet type.
 * Bike has no type API, so it resolves to an empty list.
 */
export const getServiceCategoryTypes = (
  category: SERVICE_CATEGORY | number,
  callback: Function = () => {}
) => {
  return (dispatch: Function) => {
    const resolvedCategory = toServiceCategory(category);

    if (!hasCategoryTypeFilter(resolvedCategory)) {
      callback([]);
      return;
    }

    const saveOptions = (data: any) => {
      const options = (data?.result || []).map((item: any) =>
        mapCategoryTypeItem(item, resolvedCategory)
      );
      callback(options);
    };

    const listParams = { page: 1, limit: 100 };

    if (resolvedCategory === SERVICE_CATEGORY.CAR) {
      dispatch(getAllCarTypesList(listParams, saveOptions));
    } else if (resolvedCategory === SERVICE_CATEGORY.SOFA) {
      dispatch(getSofaType(listParams, saveOptions));
    } else if (resolvedCategory === SERVICE_CATEGORY.TANK) {
      dispatch(getTankType(listParams, saveOptions));
    } else if (resolvedCategory === SERVICE_CATEGORY.CARPET) {
      dispatch(getCarpetType(listParams, saveOptions));
    }
  };
};

/** POST /api/v1/shinology/services/create-services */
export const AddServiceType = (values: any, callback: Function) => {
  return (dispatch: Function) => {
    dispatch({
      type: UPDATE_SERVICE_TYPE_REDUCER,
      payload: { loader: true },
    });
    POST_API_CALL(
      ENDPOINTS.SERVICE.CREATE,
      values,
      (response: APIResponseModal) => {
        if (response.isSuccess) {
          SHOW_TOAST(response?.data?.message, "success");
        }
        dispatch({
          type: UPDATE_SERVICE_TYPE_REDUCER,
          payload: { loader: false },
        });
        callback(response);
      }
    );
  };
};

/** GET /api/v1/shinology/services/service-detail/{id} */
export const getServiceDetail = (serviceId: string, callback: Function) => {
  return (dispatch: Function) => {
    if (!serviceId) {
      callback({ isSuccess: false, data: null });
      return;
    }

    dispatch({
      type: UPDATE_SERVICE_TYPE_REDUCER,
      payload: { loader: true },
    });

    GET_API_CALL(
      `${ENDPOINTS.SERVICE.DETAIL}/${serviceId}`,
      "",
      (response: APIResponseModal) => {
        if (response.isSuccess) {
          // Prefer nested `data` when the callback still receives an envelope.
          const payload =
            response.data?.data &&
            typeof response.data.data === "object" &&
            !Array.isArray(response.data.data) &&
            (response.data.data.serviceName != null ||
              response.data.data._id != null)
              ? response.data.data
              : response.data;
          callback({ isSuccess: true, data: payload });
        } else {
          callback({ isSuccess: false, data: null });
        }

        dispatch({
          type: UPDATE_SERVICE_TYPE_REDUCER,
          payload: { loader: false },
        });
      }
    );
  };
};

/** PATCH /api/v1/shinology/services/update-services/{id} */
export const updateService = (
  serviceId: string,
  values: any,
  callback: Function
) => {
  return (dispatch: Function) => {
    if (!serviceId) {
      callback({ isSuccess: false, data: null });
      return;
    }

    dispatch({
      type: UPDATE_SERVICE_TYPE_REDUCER,
      payload: { loader: true },
    });

    PATCH_API_CALL(
      `${ENDPOINTS.SERVICE.UPDATE}/${serviceId}`,
      values,
      (response: APIResponseModal) => {
        if (response.isSuccess) {
          SHOW_TOAST(response?.data?.message, "success");
        }
        dispatch({
          type: UPDATE_SERVICE_TYPE_REDUCER,
          payload: { loader: false },
        });
        callback(response);
      }
    );
  };
};
